import type {JsonObjectUtils} from "@cm/utils/json-object"
import type {EncodedConvertedNode, Format, MapsNodes, Resolution, SourceInfo, SourceInfoRequest, NormalY} from "#material-maps-exporter/core/types"

// Domain vocabulary

const metalnessRoughnessMaps = ["diffuse", "metalness", "roughness", "specular"] as const
const specularGlossinessMaps = ["diffuse", "reflect", "gloss", "fresnel-ior"] as const
const singleAnisotropyMaps = ["anisotropy"] as const
const splitAnisotropyMaps = ["anisotropy-strength", "anisotropy-rotation"] as const

export const TEXTURE_TYPES_FOR_ENGINE = {
    metalnessRoughness: {
        cm: [...metalnessRoughnessMaps, "normal", ...singleAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        corona: [...metalnessRoughnessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        vray: [...metalnessRoughnessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        cycles: [...metalnessRoughnessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
    },
    specularGlossiness: {
        cm: [...specularGlossinessMaps, "normal", "anisotropy", "displacement", "transmission", "alpha"] as const,
        corona: [...specularGlossinessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        vray: [...specularGlossinessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        cycles: [...specularGlossinessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
    },
} as const

export const engines = ["cm", "corona", "vray", "cycles"] as const
export type Engine = (typeof engines)[number]

export const workflows = ["metalnessRoughness", "specularGlossiness"] as const
export type Workflow = (typeof workflows)[number]

export type TextureType = (typeof TEXTURE_TYPES_FOR_ENGINE)[Workflow][keyof (typeof TEXTURE_TYPES_FOR_ENGINE)[Workflow]] extends readonly (infer T)[]
    ? T
    : never

export type ConvertedMap = {
    type: "convertedMap"
    name: string
    filename: string
    data?: JsonObjectUtils.DataObjectReference
}

// Config data model

export type ConversionConfig = {
    type: "conversionConfig"
    workflow: Workflow
    engine: Engine
    format: Format
    normalY: NormalY
    resolution: Resolution
    maps: ConvertedMap[]
}

export type TextFile = {
    type: "exportTextFile"
    name: string
    content: string
}

export type JsonFile = {
    type: "exportJsonFile"
    name: string
    content: Record<string, string>
}

export type Folder<T = ConversionConfig | TextFile | JsonFile> = {
    type: "exportFolder"
    name: string
    content: (T | Folder<T>)[]
}

export type Config = {
    type: "exportConfig"
    schema: string
    materialId: number
    sourceInfo: SourceInfo
    state: "processing" | "done"
    root: Folder
    output?: JsonObjectUtils.DataObjectReference
}

export type ConversionRequest = Omit<ConversionConfig, "type" | "maps"> & {
    type: "conversionRequest"
}

export type ConversionInfoRequest = {
    type: "conversionInfoRequest"
    conversionRequest: ConversionRequest
    name: string
    format: "json" | "text"
}

export type Request = {
    type: "exportRequest"
    root: Folder<ConversionRequest | ConversionInfoRequest>
    sourceInfoRequest: SourceInfoRequest
}

// Update operation types

export type UpdateArrayElement<T = any> = {type: "ArrayElementUpdate"} & ({idx: number; option: "overwrite"; value: T} | {option: "append"; value: T})
export type UpdateStructField<K extends string = string, T extends Record<K, any> = Record<K, any>> = {type: "StructFieldUpdate"; key: K; value: T[K]}
type UpdateArrayElementDescend = {type: "ArrayElementDescend"; idx: number}
type UpdateStructFieldDescend<K extends string = string> = {type: "StructFieldDescend"; key: K}
export const UPDATE_OP = "UpdateOp" as const
export type UpdateOp = {
    type: typeof UPDATE_OP
    descendSeq: (UpdateArrayElementDescend | UpdateStructFieldDescend)[]
    update: UpdateArrayElement | UpdateStructField
}
export type RecordKeysForValueType<T, V> =
    T extends Record<string, any> ? (Required<T> extends infer T_ ? keyof {[K_ in keyof T_ as T_[K_] extends V ? K_ : never]: string} : never) : never

type ArrayElement<A> = A extends readonly (infer T)[] ? T : never
export const SRGB_TEXTURE_TYPES_FOR_ENGINE: {
    [workflow in Workflow]: {[engine in Engine]: ArrayElement<(typeof TEXTURE_TYPES_FOR_ENGINE)[workflow][engine]>[]}
} = {
    metalnessRoughness: {
        cm: ["diffuse"],
        corona: ["diffuse"],
        vray: ["diffuse"],
        cycles: ["diffuse"],
    },
    specularGlossiness: {
        cm: ["diffuse", "reflect"],
        corona: ["diffuse", "reflect"],
        vray: ["diffuse", "reflect"],
        cycles: ["diffuse", "reflect"],
    },
}

const MAPS_EXPORT_CONFIG_SCHEMA = [0, 0, 1] as const
export const MAPS_EXPORT_CONFIG_SCHEMA_STR = `(${MAPS_EXPORT_CONFIG_SCHEMA[0]}.${MAPS_EXPORT_CONFIG_SCHEMA[1]}.${MAPS_EXPORT_CONFIG_SCHEMA[2]})` as const

export type ConvertResult<E extends Engine> =
    | MapsNodes<(typeof TEXTURE_TYPES_FOR_ENGINE)["metalnessRoughness"][E], EncodedConvertedNode>
    | MapsNodes<(typeof TEXTURE_TYPES_FOR_ENGINE)["specularGlossiness"][E], EncodedConvertedNode>
